import { useEffect, useReducer, useState } from 'react'
import { FormField } from '../../../components/inputs'
import { Button } from '../../../components/button'
import useValidation from '../../../hooks/use-validation'
import { APIErrors } from '../../../api-client/enums'

const isEmpty = (entry) => (entry.value === '' ? true : false)

const initialState = {
  email: { value: '' },
  password: { value: '' },
}

const FormFields = {
  Email: 'email',
  Password: 'password',
}

const validationErrors = {
  systemError: 'System error occurred',
  userNotFound: 'User not found',
  invalidGrant: 'Invalid Credentials',
  ValidationPending: 'Validation Pending',
}

const reducer = (state, action) => ({
  ...state,
  [action.payload.field]: { value: action.payload.value },
})

export default function Login({
  onSubmit,
  disabled,
  serverErrors,
  onRegister,
  onResetPassword,
  onEmailConfirmationPending,
}) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [showErrors, { isFormValid, errors }] = useValidation(state, validate)
  const [mappedServerErrors, setMappedServerErrors] = useState(() =>
    mapServerErrors(serverErrors?.errors),
  )

  useEffect(() => {
    setMappedServerErrors(mapServerErrors(serverErrors?.errors))
  }, [serverErrors])

  const handleChange = ({ target: { name, value } }) =>
    dispatch({ payload: { field: name, value } })

  const handleLoginClick = (e) => {
    e.preventDefault()

    if (isFormValid) {
      onSubmit({ email: state.email.value, password: state.password.value })
    } else {
      showErrors()
    }
  }

  useEffect(() => {
    if (mappedServerErrors[APIErrors.ValidationPending]) onEmailConfirmationPending()
  }, [mappedServerErrors, onEmailConfirmationPending])

  return (
    <form className='wrapper' onSubmit={handleLoginClick}>
      <div className='flex alignCenter flexDirectionColumn'>
        <h2 className='title'>Login</h2>
        <p className='signUpInstead'>
          Don't have an account?{' '}
          <button type='button' onClick={onRegister} className='flatBtn'>
            Sign up
          </button>
        </p>
      </div>
      <div>
        <div className='formFieldItem'>
          <FormField
            placeholder='Email'
            type='text'
            name={FormFields.Email}
            error={errors.email}
            value={state.email.value}
            onChange={handleChange}
          />
        </div>
        <div className='formFieldItem'>
          <FormField
            placeholder='Password'
            type='password'
            name={FormFields.Password}
            error={errors.password}
            value={state.password.value}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className='flex flexDirectionColumn alignCenter'>
        {mappedServerErrors[APIErrors.InvalidSignInCredentials] && (
          <p className='signInErrorText'>
            {mappedServerErrors[APIErrors.InvalidSignInCredentials]}
          </p>
        )}
        <p className='resetPassword '>
          Forgot your password?{' '}
          <button type='button' className='flatBtn' onClick={onResetPassword}>
            Reset your password
          </button>
        </p>
        <Button type='submit' disabled={disabled} className='login'>
          Login
        </Button>
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 2rem 4.1rem;
          background: #fff;
        }

        .title {
          padding: 0 0 0.8rem;
          line-height: 3.6rem;
          font-size: 3.2rem;
        }

        .signUpInstead {
          padding: 0 0 2.3rem;
          line-height: 2.1rem;
          font-size: 1.6rem;

          button {
            color: var(--primary-color);
          }
        }

        .signInErrorText {
          padding: 2.5rem 0 0;
          color: var(--alert-color);
          line-height: 2.1rem;
          font-size: 1.6rem;
        }

        .formFieldItem + .formFieldItem {
          padding: 1.7rem 0 0;
        }

        .resetPassword {
          text-align: center;
          padding: 2.5rem 0 1.4rem;
          line-height: 1.9rem;
          font-size: 1.4rem;

          button {
            color: var(--primary-color);
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            padding: 1.6rem 2rem 4.1rem;
          }

          .title {
            padding: 0 0 0.5rem;
            line-height: 2.4rem;
            font-size: 1.8rem;
          }

          :global(.login) {
            width: calc(100% - 2.8rem);
            position: fixed;
            bottom: 2.1rem;
          }
        }
      `}</style>
    </form>
  )
}

function validate(state) {
  const errors = {}
  let isValid = true

  for (const field of Object.keys(state)) {
    if (isEmpty(state[field])) {
      errors[field] = true
      isValid = false
    }
  }

  return { isValid, errors }
}

function mapServerErrors(errors) {
  if (!errors) return {}

  const mappedErrors = {}

  errors.forEach((error) => {
    switch (parseInt(error?.code)) {
      case APIErrors.InvalidGrant:
        mappedErrors[error.code] = validationErrors.invalidGrant
        break
      case APIErrors.ValidationPending:
        mappedErrors[error.code] = validationErrors.ValidationPending
        break
      case APIErrors.InvalidSignInCredentials:
        mappedErrors[error.code] = validationErrors.invalidGrant
        break
      default:
        mappedErrors['general'] = validationErrors.systemError
        break
    }
  })

  return mappedErrors
}
